import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarTab } from "../psychologist/tabs";
import { usePsychologistData } from "../../hooks/usePsychologistData";
import { useAppShell } from "../../contexts/AppShellContext";
import { usePsychologistContext } from "../../contexts/PsychologistContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAcuityMigration } from "../../contexts/AcuityMigrationContext";
import CircularProgress from "../../components/common/CircularProgress";
import TherapeuticStylePromptModal from "../../components/psychologist/calendar/TherapeuticStylePromptModal";

const AcuityConfiguringCard: React.FC = () => (
  <div className="flex justify-center items-center min-h-[60vh] px-4">
    <div className="max-w-md text-center border border-border rounded-2xl bg-white p-8 shadow-sm">
      <div className="flex justify-center mb-4">
        <CircularProgress sizeRem={3} />
      </div>
      <h3 className="text-lg font-semibold mb-2">Estamos configurando tu agenda</h3>
      <p className="text-sm text-muted-foreground">
        Trayendo los horarios desde Acuity. En unos segundos vas a poder ver y configurar tu calendario.
      </p>
    </div>
  </div>
);

export const CalendarPage = () => {
  const psychologist = usePsychologistData();
  const { psychologist: ctxPsychologist, setPsychologist, loadPsychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!ctxPsychologist?.id && psychologistId && !loadedRef.current) {
      loadedRef.current = true;
      setPsychologist({ id: psychologistId, specialties: [] });
      loadPsychologist(psychologistId).catch(() => { /* silencioso */ });
    }
  }, [ctxPsychologist?.id, psychologistId, setPsychologist, loadPsychologist]);
  const { setCalendarTourRunning } = useAppShell();
  const [searchParams, setSearchParams] = useSearchParams();
  const preloadProcessId = searchParams.get("process") || undefined;
  const autoOpenPatientEmail = searchParams.get("openForPatient") || undefined;
  const autoOpenDate = searchParams.get("date") || undefined;
  // Deep-link: motivo precargado para el bloqueo (ej. el nombre del feriado, desde Comunidad)
  const initialConfigReason = searchParams.get("reason") || undefined;
  // Deep-link: modo de configuración de horarios (recurring | specific-day | block)
  const rawConfig = searchParams.get("config") || undefined;
  const initialConfig = (["recurring", "specific-day", "block"] as const).includes(rawConfig as never)
    ? (rawConfig as "recurring" | "specific-day" | "block")
    : undefined;
  // Deep-link: abrir el modal de una sesión concreta (acompañar con &date= para posicionar la semana)
  const autoOpenSessionId = searchParams.get("session") || undefined;
  const autoOpenSessionView = searchParams.get("view") || undefined;
  // Deep-link compartible: abrir el modal de agendar con el paciente preseleccionado (?book=1 + ?process=)
  const autoOpenSchedule = searchParams.get("book") === "1";

  const clearPreloadPatient = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("patient");
      // En un link de agendar compartible (?book=1) mantenemos ?process= para no romper el link
      if (next.get("book") !== "1") next.delete("process");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Refleja el modo agendar en la URL para poder compartir el link (?process=<id>&book=1).
  // Solo ESCRIBE (nunca borra), para no pisar el deep-link antes de que el preload lo consuma.
  // El guard de cambio evita un loop de navegación.
  const handleBookingStateChange = useCallback((processId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("process", processId);
    next.set("book", "1");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Al terminar el agendamiento (confirmado o cancelado) limpiamos la URL y la
  // dejamos en /calendar, para que al recargar no se reabra el modo agendar.
  const clearBooking = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("book");
      next.delete("process");
      next.delete("patient");
      next.delete("openForPatient");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearAutoOpen = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("openForPatient");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearConfig = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("config");
      next.delete("reason");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearSessionOpen = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("session");
      next.delete("view");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { isInProgress, isPendingReview, welcomeDismissed } = useAcuityMigration();

  if (isInProgress) {
    return <AcuityConfiguringCard />;
  }

  // Si la psico ya vio (o cerró) el modal de bienvenida y aún quedan horarios
  // migrados sin revisar, abrimos el modo edición de horario fijo al entrar a /calendar.
  // Cuando dismissea el review modal, pending_review pasa a false y deja de aplicarse.
  const effectiveInitialConfig =
    initialConfig ?? (welcomeDismissed && isPendingReview ? "recurring" : undefined);

  return (
    <>
      <TherapeuticStylePromptModal />
      <CalendarTab
        psychologist={psychologist}
        onTourRunningChange={setCalendarTourRunning}
        preloadProcessId={preloadProcessId}
        onPreloadConsumed={clearPreloadPatient}
        autoOpenPatientEmail={autoOpenPatientEmail}
        autoOpenDate={autoOpenDate}
        onAutoOpenConsumed={clearAutoOpen}
        initialConfig={effectiveInitialConfig}
        initialConfigDate={autoOpenDate}
        initialConfigReason={initialConfigReason}
        onConfigConsumed={clearConfig}
        autoOpenSessionId={autoOpenSessionId}
        autoOpenSessionView={autoOpenSessionView}
        onAutoOpenSessionConsumed={clearSessionOpen}
        autoOpenSchedule={autoOpenSchedule}
        onBookingStateChange={handleBookingStateChange}
        onBookingEnded={clearBooking}
      />
    </>
  );
};
